import { assertLayoutNodeExhaustive } from './layoutAssertNever';
import { walkLayoutStructureChildren } from './layoutStructureChildLists';
import type { LayoutNode } from './types';

/** User-visible copy declared in layout JSON (not schema prompts, not `qid` attributes). */
export function collectLayoutUserVisibleText(nodes: LayoutNode[]): string[] {
  const chunks: string[] = [];

  const pushLabelOverride = (node: { labelOverride?: string }): void => {
    const text = node.labelOverride?.trim();
    if (text) chunks.push(text);
  };

  const pushTableSlotText = (slot: string | LayoutNode[] | undefined): void => {
    if (typeof slot !== 'string') return;
    const text = slot.trim();
    if (text) chunks.push(text);
  };

  const walk = (arr: LayoutNode[]) => {
    for (const node of arr) {
      switch (node.type) {
        case 'static':
        case 'text':
        case 'section-label':
          if (node.text.trim()) chunks.push(node.text);
          break;
        case 'display':
          if (node.label?.trim()) chunks.push(node.label);
          break;
        case 'external-answer':
          if (node.label?.trim()) chunks.push(node.label);
          if (node.empty?.trim()) chunks.push(node.empty);
          break;
        case 'table':
          // Node-valued headers and labels contribute their text through the
          // structural walk below, like any other node; only the plain-string
          // spelling has text to collect here.
          pushTableSlotText(node.rowHeader);
          for (const header of node.colHeaders) pushTableSlotText(header);
          for (const row of node.rows) pushTableSlotText(row.label);
          break;
        case 'OASISRadio':
        case 'OASISYesNoCheckbox':
        case 'OASISSelect':
        case 'OASISText':
        case 'OASISPhysician':
        case 'OASISDate':
        case 'OASISTime':
        case 'OASISNumber':
        case 'OASISMultiselect':
        case 'OASISMultiselectOption':
          pushLabelOverride(node);
          break;
        case 'card':
        case 'stack':
        case 'grid':
        case 'flex':
        case 'box':
        case 'indent':
        case 'field-row':
        case 'conditional':
        case 'disabled-when':
        case 'repeat':
        case 'divider':
        case 'sum':
        case 'radio-option':
          break;
        default:
          assertLayoutNodeExhaustive(node);
      }
      walkLayoutStructureChildren(node, walk);
    }
  };

  walk(nodes);
  return chunks;
}

export function layoutUserVisibleTextIncludes(
  chunks: readonly string[],
  literal: string
): boolean {
  if (!literal) return false;
  return chunks.some(chunk => chunk.includes(literal));
}
